import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Mail, Calendar, AlertCircle, CheckCircle, Clock, User, ChevronRight } from 'lucide-react';
import { getStewardedCapsules, deliverCapsule, completeMilestone } from '../services/api';
import Layout from '../components/common/Layout';
import { toast } from 'react-toastify';

// Color constants
const colors = {
  primaryGreen: '#556B2F',
  secondaryGreen: '#6B8E23',
  highlightOrange: '#E65100',
  backgroundCream: '#F5F5DC',
  textDark: '#333333',
  textMedium: '#666666',
  textMuted: '#999999',
  warningYellow: '#F59E0B',
  successGreen: '#10B981',
};

// Status badge component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    'DECEASED': { bg: '#FEE2E2', text: '#991B1B', label: 'Passed Away' },
    'UNABLE': { bg: '#FEF3C7', text: '#92400E', label: 'Unable to Act' },
    'ACTIVE': { bg: '#D1FAE5', text: '#065F46', label: 'Active' },
  };

  const config = statusConfig[status] || statusConfig['ACTIVE'];

  return (
    <span
      className="px-2 py-1 rounded-full text-xs font-medium"
      style={{ backgroundColor: config.bg, color: config.text }}
    >
      {config.label}
    </span>
  );
};

// Capsule card component
const CapsuleCard = ({ capsule, onDeliver, delivering }) => {
  const navigate = useNavigate();

  const getMilestoneStyle = (milestoneName) => {
    const name = (milestoneName || '').toLowerCase();
    if (name.includes('birthday') || name.includes('18')) return { icon: '🎂', bg: '#FFF8E1' };
    if (name.includes('graduation')) return { icon: '🎓', bg: '#E8F5E9' };
    if (name.includes('wedding')) return { icon: '💒', bg: '#FCE4EC' };
    if (name.includes('baby') || name.includes('child')) return { icon: '👶', bg: '#E3F2FD' };
    if (name.includes('job')) return { icon: '💼', bg: '#FFF3E0' };
    return { icon: '🎯', bg: '#F5F5F0' };
  };

  const milestoneStyle = getMilestoneStyle(capsule.milestone_name);
  const formattedDate = capsule.fallback_date
    ? new Date(capsule.fallback_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : 'When milestone completes';

  return (
    <div className="bg-white rounded-xl shadow-sm p-5 hover:shadow-md transition-shadow">
      {/* Header: From person */}
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ backgroundColor: '#F0F4E8' }}
        >
          <span className="text-lg">💌</span>
        </div>
        <div className="flex-1">
          <div className="text-sm" style={{ color: colors.textMuted }}>Letter from</div>
          <div className="font-medium" style={{ color: colors.textDark }}>
            {capsule.sender_first_name} {capsule.sender_last_name}
          </div>
        </div>
        <StatusBadge status={capsule.sender_availability_status} />
      </div>

      {/* To and When */}
      <div className="flex gap-4 mb-4">
        <div className="flex-1">
          <div className="text-xs uppercase tracking-wide mb-1" style={{ color: colors.textMuted }}>To</div>
          <div className="flex items-center gap-2">
            <User size={16} style={{ color: colors.textMedium }} />
            <span style={{ color: colors.textDark }}>
              {capsule.receiver_first_name} {capsule.receiver_last_name}
            </span>
          </div>
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-wide mb-1" style={{ color: colors.textMuted }}>Deliver On</div>
          <div className="flex items-center gap-2">
            <span className="text-lg">{milestoneStyle.icon}</span>
            <span style={{ color: colors.textDark }}>
              {capsule.milestone_name || formattedDate}
            </span>
          </div>
        </div>
      </div>

      {/* Milestone status if waiting */}
      {capsule.milestone_status && capsule.milestone_status !== 'COMPLETED' && (
        <div
          className="rounded-lg p-3 mb-4"
          style={{ backgroundColor: milestoneStyle.bg }}
        >
          <div className="flex items-center gap-2">
            <Clock size={16} style={{ color: colors.warningYellow }} />
            <span className="text-sm" style={{ color: colors.textMedium }}>
              Waiting for milestone: <strong>{capsule.milestone_name}</strong>
              {capsule.milestone_target_date && (
                <span> (Expected: {new Date(capsule.milestone_target_date).toLocaleDateString()})</span>
              )}
            </span>
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-3">
        {capsule.can_deliver_now ? (
          <button
            onClick={() => onDeliver(capsule)}
            disabled={delivering === capsule.id}
            className="flex-1 px-4 py-3 rounded-xl text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ backgroundColor: colors.primaryGreen }}
          >
            {delivering === capsule.id ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                Delivering...
              </>
            ) : (
              <>
                <Mail size={18} />
                Deliver Now
              </>
            )}
          </button>
        ) : (
          <div
            className="flex-1 px-4 py-3 rounded-xl flex items-center justify-center gap-2"
            style={{ backgroundColor: '#F5F5F0', color: colors.textMuted }}
          >
            <Clock size={18} />
            Awaiting Milestone
          </div>
        )}
      </div>

      {/* Reason for authorization */}
      {capsule.delivery_reason && (
        <div className="mt-3 text-xs" style={{ color: colors.textMuted }}>
          {capsule.delivery_reason === 'steward' && capsule.steward_role === 'backup' &&
            'You can deliver as backup steward because the sender is unavailable and there is no primary steward able to act'}
          {capsule.delivery_reason === 'steward' && capsule.steward_role !== 'backup' &&
            'You can deliver as steward because the sender is unavailable'}
          {capsule.delivery_reason === 'sender' && 'You created this letter'}
        </div>
      )}
    </div>
  );
};

// Summary card component
const SummaryCard = ({ summary }) => {
  if (!summary) return null;

  return (
    <div className="bg-white rounded-xl shadow-sm p-5 mb-6">
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ backgroundColor: '#F0F4E8' }}
        >
          <Shield size={20} style={{ color: colors.primaryGreen }} />
        </div>
        <div>
          <h2 className="font-semibold" style={{ color: colors.textDark }}>Your Stewardship Summary</h2>
          <p className="text-sm" style={{ color: colors.textMuted }}>Letters you're trusted to deliver</p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="text-center p-3 rounded-lg" style={{ backgroundColor: '#F5F5F0' }}>
          <div className="text-2xl font-bold" style={{ color: colors.primaryGreen }}>
            {summary.total_stewarding || 0}
          </div>
          <div className="text-xs" style={{ color: colors.textMuted }}>Total Stewarding</div>
        </div>
        <div className="text-center p-3 rounded-lg" style={{ backgroundColor: '#D1FAE5' }}>
          <div className="text-2xl font-bold" style={{ color: '#065F46' }}>
            {summary.active_senders || 0}
          </div>
          <div className="text-xs" style={{ color: '#065F46' }}>Senders Active</div>
        </div>
        <div className="text-center p-3 rounded-lg" style={{ backgroundColor: summary.needs_attention ? '#FEF3C7' : '#F5F5F0' }}>
          <div className="text-2xl font-bold" style={{ color: summary.needs_attention ? '#92400E' : colors.textDark }}>
            {summary.ready_to_deliver || 0}
          </div>
          <div className="text-xs" style={{ color: summary.needs_attention ? '#92400E' : colors.textMuted }}>Ready to Deliver</div>
        </div>
        <div className="text-center p-3 rounded-lg" style={{ backgroundColor: '#F5F5F0' }}>
          <div className="text-2xl font-bold" style={{ color: colors.textDark }}>
            {summary.already_delivered || 0}
          </div>
          <div className="text-xs" style={{ color: colors.textMuted }}>Already Delivered</div>
        </div>
      </div>

      {summary.needs_attention && (
        <div
          className="mt-4 p-3 rounded-lg flex items-center gap-2"
          style={{ backgroundColor: '#FEF3C7' }}
        >
          <AlertCircle size={18} style={{ color: '#92400E' }} />
          <span className="text-sm" style={{ color: '#92400E' }}>
            You have letters waiting to be delivered. The sender is no longer available.
          </span>
        </div>
      )}
    </div>
  );
};

// Main component
const StewardedLetters = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [capsules, setCapsules] = useState([]);
  const [summary, setSummary] = useState(null);
  const [delivering, setDelivering] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getStewardedCapsules();
      setCapsules(data.capsules || []);
      setSummary(data.summary || null);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch stewarded capsules:', err);
      setError(err.message || 'Failed to load stewarded letters');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDeliver = async (capsule) => {
    try {
      setDelivering(capsule.id);
      await deliverCapsule(capsule.id);
      toast.success(`Letter delivered to ${capsule.receiver_first_name}!`);
      // Refresh the list
      await fetchData();
    } catch (err) {
      console.error('Failed to deliver capsule:', err);
      toast.error(err.message || 'Failed to deliver letter');
    } finally {
      setDelivering(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.backgroundCream }}>
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: colors.primaryGreen }} />
            <p style={{ color: colors.textMedium }}>Loading stewarded letters...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.backgroundCream }}>
          <div className="text-center">
            <AlertCircle size={48} style={{ color: colors.highlightOrange }} className="mx-auto mb-4" />
            <p className="mb-4" style={{ color: colors.textDark }}>{error}</p>
            <button
              onClick={fetchData}
              className="px-6 py-3 rounded-xl text-white font-medium"
              style={{ backgroundColor: colors.primaryGreen }}
            >
              Try Again
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen" style={{ backgroundColor: colors.backgroundCream }}>
        {/* Header */}
        <div
          className="-mx-4 -mt-4 sm:-mx-6 lg:-mx-8 px-6 py-6"
          style={{ background: `linear-gradient(135deg, ${colors.primaryGreen} 0%, ${colors.secondaryGreen} 100%)` }}
        >
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 text-white mb-2">
              <Shield size={28} />
              <h1 className="text-2xl font-bold">Stewarded Letters</h1>
            </div>
            <p className="text-white/80">
              Letters you've been trusted to deliver on behalf of family members
            </p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-6">
          {/* Summary Card */}
          <SummaryCard summary={summary} />

          {/* Capsules List */}
          {capsules.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm p-8 text-center">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: '#F0F4E8' }}
              >
                <Shield size={32} style={{ color: colors.primaryGreen }} />
              </div>
              <h3 className="text-lg font-semibold mb-2" style={{ color: colors.textDark }}>
                No Letters Needing Attention
              </h3>
              <p className="mb-4" style={{ color: colors.textMuted }}>
                When family members designate you as a steward for their letters, they'll appear here if they're unable to deliver them personally.
              </p>
              <button
                onClick={() => navigate('/timecapsule')}
                className="px-6 py-3 rounded-xl font-medium border-2"
                style={{ borderColor: colors.primaryGreen, color: colors.primaryGreen }}
              >
                Go to Letters
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold" style={{ color: colors.textDark }}>
                Letters Ready for Delivery ({capsules.filter(c => c.can_deliver_now).length})
              </h3>
              {capsules.map((capsule) => (
                <CapsuleCard
                  key={capsule.id}
                  capsule={capsule}
                  onDeliver={handleDeliver}
                  delivering={delivering}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default StewardedLetters;
